import { faLink, faPlus } from "@fortawesome/pro-regular-svg-icons";
import {
  Badge,
  Grid,
  Icon,
  Inline,
  Message,
  Section,
  Tooltip,
} from "@intility/bifrost-react";
import Menu from "@intility/bifrost-react/Menu";
import { useQueries } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { clientQuery } from "../../../api/clients";
import type { ClientListItem } from "../../../api/types";
import { ClientObject } from "../../../components/ClientObject";
import { ClientPurposeBadge } from "../../../components/ClientTypeBadge";
import {
  apiClientName,
  NewApiClientForm,
} from "../../../components/NewApiClientForm";
import { NewClientForm } from "../../../components/NewClientForm";
import "./PlatformClientsTab.css";

// An icon-only badge is a circle as wide as a normal badge is tall: its line
// height plus the badge's vertical padding (the same as in the Management
// section).
const ICON_BADGE_SIDE = "calc(1lh + 2 * var(--bfs2))";
const ICON_BADGE_STYLE = {
  display: "inline-grid",
  placeItems: "center",
  width: ICON_BADGE_SIDE,
  height: ICON_BADGE_SIDE,
  padding: 0,
} as const;

// The menu's "New client" entry, picked in place of a client id.
const NEW_CLIENT = "new";
// The menu's entry for the API that "Auto create" leaves to fill in.
const DRAFT_API = "draft-api";

export function PlatformClientsTab({
  platformId,
  platformName,
  requestedClientId = null,
  clients,
  themeClass,
}: {
  /** The platform the clients belong to; a new client is created in it. */
  platformId: string;
  /** The platform's name, which a new client without a name is named after. */
  platformName: string;
  /** A client to open, from a link to it (`?client=<id>` on the platform page). */
  requestedClientId?: string | null;
  clients: ClientListItem[];
  /** The platform's Bifrost theme class, for parts of a client that keep it. */
  themeClass?: string;
}) {
  // A linked client is selected first. A platform with no clients opens on "New
  // client". Picked outright, it stays selected while the first clients are
  // being created, so the form (and any error in it) is not replaced by a
  // client that was created before a failure.
  const [selectedId, setSelectedId] = useState<string | null>(
    () => requestedClientId ?? (clients.length === 0 ? NEW_CLIENT : null),
  );
  // A link to another client of this platform, while the tab is open.
  useEffect(() => {
    if (requestedClientId) setSelectedId(requestedClientId);
  }, [requestedClientId]);
  // A platform with no clients has only "New client" to pick, so it is the
  // selected entry.
  const creating = selectedId === NEW_CLIENT || clients.length === 0;
  // The API left to fill in by "Auto create": a menu entry until it is saved.
  const [draftApi, setDraftApi] = useState(false);
  const showingDraft = draftApi && selectedId === DRAFT_API;
  // Falls back to the first client when nothing is picked yet, or when the
  // picked one is gone (deleted or moved off this platform). "New client" and
  // the API draft are not clients: nothing is selected then.
  const selected =
    creating || showingDraft
      ? undefined
      : (clients.find((c) => c.id === selectedId) ?? clients[0]);
  // The platform counts as wired up once every one of its clients is. The
  // wired-up claim is durable on each client (ADR-0060), so the banner shows
  // how far along the platform is, from the clients the platform detail
  // already returns.
  const wiredCount = clients.filter((c) => c.wiredUpAt != null).length;
  const notWiredUp = clients.length > 0 && wiredCount < clients.length;
  // The notice asks someone to wire the clients up, so it is for people who can
  // edit at least one of them. A client's role comes from its full record (the
  // list item's role is only the fallback while that loads).
  const details = useQueries({
    queries: clients.map((c) => clientQuery(c.id)),
  });
  const canEditAny = clients.some((c, i) => {
    const role = details[i]?.data?.userRole ?? c.userRole;
    return role === "owner" || role === "maintainer";
  });

  return (
    <Grid gap={24}>
      <div>
        <h2 className="bf-h2" style={{ margin: 0 }}>
          Clients
        </h2>
        <p className="bf-large bfc-base-2" style={{ margin: "4px 0 0" }}>
          Clients connected to this platform
        </p>
      </div>
      <div className="platform-clients-columns">
        <div className="platform-clients-menu">
          <Section border radius background="base-3">
            <Section.Content>
              <Menu>
                {clients.map((client) => (
                  <button
                    key={client.id}
                    type="button"
                    className={client.id === selected?.id ? "active" : ""}
                    onClick={() => setSelectedId(client.id)}
                  >
                    <Menu.Item>
                      <Inline align="center">
                        <ClientPurposeBadge
                          appType={client.appType}
                          purposeTag={client.purposeTag}
                        />
                        <Inline.Stretch>{client.name}</Inline.Stretch>
                        {/* A client that is not wired up yet. */}
                        {!client.wiredUpAt && (
                          <Tooltip content="Not wired up">
                            <Badge
                              pill
                              state="warning"
                              aria-label="Not wired up"
                              style={ICON_BADGE_STYLE}
                            >
                              <Icon icon={faLink} />
                            </Badge>
                          </Tooltip>
                        )}
                      </Inline>
                    </Menu.Item>
                  </button>
                ))}
                {/* The API that "Auto create" leaves to fill in. It is not a
                    client yet: it exists in the page until it is saved. */}
                {draftApi && (
                  <button
                    type="button"
                    className={showingDraft ? "active" : ""}
                    onClick={() => setSelectedId(DRAFT_API)}
                  >
                    <Menu.Item>
                      <Inline align="center">
                        <ClientPurposeBadge appType="api" purposeTag="api" />
                        <Inline.Stretch>
                          {apiClientName(platformName)}
                        </Inline.Stretch>
                        <Badge pill state="alert">
                          Not created
                        </Badge>
                      </Inline>
                    </Menu.Item>
                  </button>
                )}
                <button
                  type="button"
                  className={creating ? "active" : ""}
                  onClick={() => setSelectedId(NEW_CLIENT)}
                >
                  <Menu.Item>
                    <Inline align="center">
                      <Icon icon={faPlus} />
                      <Inline.Stretch>New client</Inline.Stretch>
                    </Inline>
                  </Menu.Item>
                </button>
              </Menu>
            </Section.Content>
          </Section>
          {/* Under the menu, 16px from it. */}
          {notWiredUp && canEditAny && (
            <Message
              state="warning"
              header="Platform not wired up"
              style={{ marginTop: 16 }}
            >
              {wiredCount} of {clients.length} clients are wired up. Wire up
              each client to your code to connect the platform to Intility ID.
            </Message>
          )}
        </div>
        <div className="platform-clients-selected">
          {/* Keyed on the client, so switching clients starts the object
              fresh instead of carrying one client's inputs, open drawers or
              dropdowns over to the next. */}
          {creating && (
            <NewClientForm
              platformId={platformId}
              platformName={platformName}
              suggestAutoCreate={clients.length === 0}
              onAutoCreated={() => {
                setDraftApi(true);
                setSelectedId(DRAFT_API);
              }}
              onCreated={(client) => setSelectedId(client.id)}
              // Back to the clients. A platform with none has nothing to go
              // back to, so it has no cancel.
              onCancel={
                clients.length > 0 ? () => setSelectedId(null) : undefined
              }
            />
          )}
          {showingDraft && (
            <NewApiClientForm
              platformId={platformId}
              platformName={platformName}
              onCreated={(client) => {
                setDraftApi(false);
                setSelectedId(client.id);
              }}
            />
          )}
          {selected && (
            <ClientObject
              key={selected.id}
              client={selected}
              fill
              platformThemeClass={themeClass}
            />
          )}
        </div>
      </div>
    </Grid>
  );
}
